import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { useMemo } from "react"
import { useSelector } from "react-redux"
import { Claim, Sku, Product } from "../api/models"
import { addEntityFetchReducers, EntityState } from "./entity"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import Api from "../api/api"
import { FetchableState, useFetchableState } from "./fetchableState"
import { usePoints } from "./pointsSlice"
import { CROSSGRADE_OFFERS_ENABLED, REWARD_POINTS_ENABLED, UPGRADE_OFFERS_ENABLED } from "../featureFlags"

// Shipped skus sold in the store, keyed by skuId. Kept separate from
// skusSlice because these entities carry the per-user `upgrade` offer,
// which skus hydrated by other thunks don't have.

export type StoreSkusCollectionState = FetchableState<string[]>

interface StoreState extends EntityState<Sku> {
    collection: StoreSkusCollectionState,
    claimRewardRequestId?: string,
    debug: string
}

const initialState: StoreState = {
    entities: {},
    collection: {
        data: undefined,
        currentRequestId: undefined,
        lastFetched: undefined
    },
    claimRewardRequestId: undefined,
    debug: "store state",
}

export const storeSlice = createSlice({
    name: 'store',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            Sku,
            { sku: Sku, claims: Claim[], products: Product[] },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.sku,
            storeSkuThunk
        )

        builder.addCase(storeSkusThunk.pending, (state, action) => {
            if (state.collection.currentRequestId)
                return
            state.collection.currentRequestId = action.meta.requestId
            storeSkusCollectionReducerIntercept(storeSkusThunk.pending, state, action)
        })
        builder.addCase(storeSkusThunk.fulfilled, (state, action) => {
            if (state.collection.currentRequestId !== action.meta.requestId)
                return
            state.collection.currentRequestId = undefined
            state.collection.data = action.payload.skus.map((sku: Sku) => sku.id)
            storeSkusCollectionReducerIntercept(storeSkusThunk.fulfilled, state, action)
        })
        builder.addCase(storeSkusThunk.rejected, (state, action) => {
            if (state.collection.currentRequestId !== action.meta.requestId)
                return
            state.collection.currentRequestId = undefined
            storeSkusCollectionReducerIntercept(storeSkusThunk.rejected, state, action)
        })

        builder.addCase(claimRewardThunk.pending, (state, action) => {
            if (state.claimRewardRequestId)
                return
            state.claimRewardRequestId = action.meta.requestId
        })
        builder.addCase(claimRewardThunk.fulfilled, (state, action) => {
            if (state.claimRewardRequestId !== action.meta.requestId)
                return
            state.claimRewardRequestId = undefined
        })
        builder.addCase(claimRewardThunk.rejected, (state, action) => {
            if (state.claimRewardRequestId !== action.meta.requestId)
                return
            state.claimRewardRequestId = undefined
        })

        builder.addCase(logOut, (state: StoreState) => {
            state = initialState;
        })
    },
})


// GET /me/skus/:skuId

export const storeSkuThunk = createAsyncThunk(
    'store/fetchStoreSku',
    async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.store.entities[id]?.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getMySku({ skuId: id, accessToken })
    }
)

export const useStoreSku = (id: string, accessToken: string) => {
    return useFetchableState<Sku>({
        selector: (state: RootState) => state.store.entities[id],
        action: storeSkuThunk({ id, accessToken })
    })
}


// GET /me/store/skus

export const storeSkusThunk = createAsyncThunk(
    'store/fetchStoreSkus',
    async ({ accessToken }: { accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.store.collection.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        // the offers derivation scans the whole catalog — request the max page
        return await Api.listStoreSkus({ accessToken, count: 100 })
    }
)

const storeSkusCollectionReducerIntercept = (
    type: any,
    state: any,
    action: PayloadAction<any>
) => {
    if (type === storeSkusThunk.fulfilled) {
        const { skus } = action.payload
        skus.forEach((sku: Sku) => {
            const entity = state.entities[sku.id]
            if (entity?.currentRequestId !== undefined)
                return
            state.entities[sku.id] = {
                data: sku,
                lastFetched: Date.now()
            }
        })
    }
}

export const useStoreSkus = (accessToken: string) => {
    return useFetchableState<string[]>({
        selector: (state: RootState) => state.store.collection,
        action: storeSkusThunk({ accessToken })
    })
}


// The ONE personalized one-time price a sku shows: the backend emits upgrade
// (bundle trade-in) and crossgrade (owned outside products) independently,
// and every surface — Offers sections, Store card, Dashboard teaser — picks
// the cheaper of the kinds this build enables, tie → upgrade. Checkout then
// echoes whichever was shown; the server verifies either on its own.
export type PersonalOffer =
    | { kind: "upgrade", priceCents: number, upgrade: NonNullable<Sku["upgrade"]> }
    | { kind: "crossgrade", priceCents: number, crossgrade: NonNullable<Sku["crossgrade"]> }

export function personalOffer(sku: Sku): PersonalOffer | undefined {
    const upgrade = UPGRADE_OFFERS_ENABLED && sku.upgrade !== undefined
        ? { kind: "upgrade" as const, priceCents: sku.upgrade.priceCents, upgrade: sku.upgrade } : undefined
    const crossgrade = CROSSGRADE_OFFERS_ENABLED && sku.crossgrade !== undefined
        ? { kind: "crossgrade" as const, priceCents: sku.crossgrade.priceCents, crossgrade: sku.crossgrade } : undefined
    if (upgrade !== undefined && crossgrade !== undefined)
        return crossgrade.priceCents < upgrade.priceCents ? crossgrade : upgrade
    return upgrade ?? crossgrade
}

// Derived "Your Offers": the store skus the user is personally eligible for,
// split into the categories surfaced on the Offers page and Dashboard teaser.
//   - upgrades: skus whose personal offer is an `upgrade` (the user owns at
//     least one product comprising the bundle).
//   - crossgrades: skus whose personal offer is a `crossgrade` with a price
//     above 0 (owned outside products earned a discount).
//   - free: skus with a free offering (claimed at no cost, no points needed)
//     — or whose crossgrade price reached 0, claimed the same way.
//   - rewards: skus claimable with points — those with a reward offering
//     whose points cost (priceCents) the current balance covers.
// All read from the entities the collection thunk already populated, so this
// hook does no fetching beyond triggering the collection + points loads.
export interface Offers {
    loading: boolean
    upgrades: string[]      // skuIds
    crossgrades: string[]   // skuIds
    free: string[]          // skuIds
    rewards: string[]       // skuIds
    total: number
}

export const useOffers = (accessToken: string): Offers => {
    const { data: collection } = useStoreSkus(accessToken)
    const { data: points } = usePoints(accessToken)
    const entities = useSelector((state: RootState) => state.store.entities)

    return useMemo(() => {
        if (collection === undefined)
            return { loading: true, upgrades: [], crossgrades: [], free: [], rewards: [], total: 0 }
        const balance = points ? points.topups - points.spendings : 0
        const skus = collection
            .map((id) => entities[id]?.data)
            .filter((sku): sku is Sku => sku !== undefined)
        // FCS-hidden offer kinds are dropped by personalOffer so every
        // consumer (Offers page, Dashboard teaser, sidebar badge) agrees
        const picks = skus.map((sku) => ({ sku, offer: personalOffer(sku) }))
        const upgrades = picks.filter(({ offer }) => offer?.kind === "upgrade").map(({ sku }) => sku.id)
        const crossgrades = picks.filter(({ offer }) => offer?.kind === "crossgrade" && offer.priceCents > 0).map(({ sku }) => sku.id)
        const free = picks.filter(({ sku, offer }) =>
            sku.offerings.some(o => o.paymentType === "free")
            || (offer?.kind === "crossgrade" && offer.priceCents === 0)
        ).map(({ sku }) => sku.id)
        const rewards = REWARD_POINTS_ENABLED
            ? skus.filter((sku) =>
                !free.includes(sku.id)
                && sku.offerings.some(o =>
                    !!points && o.paymentType === "reward" && balance >= o.priceCents)
            ).map((sku) => sku.id)
            : []
        return { loading: false, upgrades, crossgrades, free, rewards, total: upgrades.length + crossgrades.length + free.length + rewards.length }
    }, [collection, entities, points])
}


// POST skus/:skuId/rewards (redeem points and claim reward)

export const claimRewardThunk = createAsyncThunk(
    'store/claimReward',
    async ({ accessToken, skuId, offeringId }: { accessToken: string, skuId: string, offeringId: string }) => {
        return await Api.claimReward({ skuId, offeringId, accessToken })
    }
)

export default storeSlice.reducer
